import type { SignedStep } from "../api/types.js";
import type { Inert } from "./describe.js";

/**
 * DCTA-143 (red-team P2 finding 1; Q13: shown = signed) — the full text of a flagged registry string.
 *
 * The bank flags a display string that looks like an instruction (`firewall.data_flags`) and gives the card its INERT
 * form: quoted, invisible characters gone, cut to 40 characters. The cut is visual only: the string is part of what the
 * customer signs, so the card must be able to show all of it. "Show the full text" (beside the bank's sentence) prints
 * each flagged string the card shows WHOLE, as the payload signs it, named by what it is — and never lets a character
 * act on the line: a zero-width space, a bidi override, a line break is printed as its code point ("U+200B").
 * Nothing here decides what is flagged (the bank's firewall does), and nothing here rewrites the signed string.
 */
export interface FullText {
  /** What the string is, in the card's words ("Bill reference"); "Step 2 · …" when strings on several steps are flagged. */
  readonly label: string;
  /** The string as signed — every character. */
  readonly full: string;
}

/** A run of the full text: printable text, or a character that is never printed as itself — named by its code point. */
export type Printed = { readonly text: string } | { readonly cp: string };

/**
 * The characters the bank's inert form drops (lab/dcta firewall/data.ts `UNPRINTED`, the nickname rule's set): C0 and
 * C1 controls, the soft hyphen, zero-width and bidi marks, line and paragraph separators, embeddings and overrides,
 * invisible operators, isolates and the byte-order mark. In the full text each one is SHOWN, as "U+XXXX".
 */
const UNPRINTED: readonly (readonly [number, number])[] = [
  [0x0000, 0x001f],
  [0x007f, 0x009f],
  [0x00ad, 0x00ad],
  [0x200b, 0x200f],
  [0x2028, 0x2029],
  [0x202a, 0x202e],
  [0x2060, 0x2064],
  [0x2066, 0x2069],
  [0xfeff, 0xfeff],
];

/** Written as numbers, never as escapes in a literal: a literal U+2028 ends a line of source. */
const unprinted = (cp: number): boolean => UNPRINTED.some(([from, to]) => cp >= from && cp <= to);

/** A string as runs of printable text and named code points, in order; nothing dropped, nothing added. */
export function printable(value: string): Printed[] {
  const out: Printed[] = [];
  let run = "";
  for (const ch of value) {
    const cp = ch.codePointAt(0) ?? 0;
    if (!unprinted(cp)) {
      run += ch;
      continue;
    }
    if (run !== "") out.push({ text: run });
    run = "";
    out.push({ cp: `U+${cp.toString(16).toUpperCase().padStart(4, "0")}` });
  }
  if (run !== "") out.push({ text: run });
  return out;
}

/**
 * What a flagged string is, in the words the card already uses for it (the opened row's "bill reference", "registered
 * name", "reference"; §F.2 13c's "Your name for them"; the sub-line's "from"). Microcopy for ux-designer to rule on.
 */
const LABELS: Readonly<Record<string, string>> = {
  "biller/name": "Biller",
  bill_reference: "Bill reference",
  reference: "Reference",
  "payee/nickname": "Your name for them",
  "payee/registered_name": "Registered name",
  "debit_account/display_name": "From account",
  "settlement_account/display_name": "From account",
  "to_account/display_name": "To account",
  "instrument/name": "Investment",
  "target/display_name": "Account",
};

export function fieldLabel(field: string): string {
  return LABELS[field] ?? "Text";
}

/**
 * The full texts behind ONE sentence of the bank (a notice): each flagged string the card prints whose flag carries that
 * sentence, once, in the order the card prints them. When the strings sit on more than one step, each is named by its
 * step's place on the card ("Step 2 · Your name for them") — the card's rows are in that order.
 */
export function fullTexts(steps: readonly SignedStep[], inert: readonly Inert[], sentence: string): FullText[] {
  const seen = new Set<string>();
  const mine = inert.filter((i) => {
    const key = `${i.stepId}|${i.field}`;
    if (i.notice !== sentence || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  const several = new Set(mine.map((i) => i.stepId)).size > 1;
  return mine.map((i) => {
    const n = steps.findIndex((s) => s.step_id === i.stepId) + 1;
    return { label: several && n > 0 ? `Step ${String(n)} · ${fieldLabel(i.field)}` : fieldLabel(i.field), full: i.full };
  });
}

/** The bank's sentences about the flagged strings a surface prints — each once, with the full texts behind it. */
export interface InertNotice {
  readonly sentence: string;
  readonly fulls: readonly FullText[];
}

/** One rule for the card and the receipt: each distinct non-blank sentence once, in the order first printed. */
export function inertNotices(steps: readonly SignedStep[], inert: readonly Inert[]): InertNotice[] {
  const sentences = [...new Set(inert.map((i) => i.notice).filter((n): n is string => n !== undefined && n.trim() !== ""))];
  return sentences.map((sentence) => ({ sentence, fulls: fullTexts(steps, inert, sentence) }));
}
